import "./qa_resume_list.page.scss";
import { FC, useRef, useState, useEffect } from "react";
import LoadingComponent from "../../../components/LoadingComponent/loading.component";
import NotResultsComponent from "../../../components/noResults/not_results.component";
import PaginatorComponent from "../../../components/paginator/PaginatorComponent";
import LayoutComponent from "../../../layouts/layoutComponent/layout.component";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import AutoCompleteWithCheckComponent from "../../../components/autocompleteWithCheck/AutoCompleteWithCheckComponent";
import ButtonComponent from "../../../components/button/button.component";
import useQAResumeListViewModel from "./qa_resume_list.view_model";
import Validators from "../../../utils/Validators";
import { ErrorMessage } from "@hookform/error-message";

const allColumns = [
  { key: "job_id", label: "Job ID" },
  { key: "audio_name", label: "Audio Name" },
  { key: "job_status", label: "Job Status" },
  { key: "qa_agent_name", label: "Scorecard Name" },
  { key: "human_agent_name", label: "Team Member Name" },
  { key: "created_at", label: "Created At" },
  { key: "agent_score", label: "Team Member Score" },
  { key: "customer_score", label: "Customer Score" },
  { key: "average_score", label: "Average Score" },
  { key: "agent_sentiment", label: "Team Member Sentiment" },
  { key: "customer_sentiment", label: "Customer Sentiment" },
  { key: "call_type", label: "Call Type" },
  { key: "target", label: "Target" },
  { key: "achieved", label: "Achieved" },
];

const QAResumeListPage: FC<{}> = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);


  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map(col => col.key)
  );

  const toggleColumn = (key: string) => {
    setVisibleColumns(prev =>
      prev.includes(key)
        ? prev.filter(col => col !== key)
        : [...prev, key]
    );
  };

  const {
    formInfo,
    result,
    i18n,
    register,
    handleSubmit,
    _handleSearch,
    _handleOnChangePage,
    _onSearchHuman,
    _onSearchCallState,
    _onSearchSentiment,
    _handleOpenVisibility,
    handleShowModal,
    handleDownloadData,
    loadingDownload,
    handleOnChangeForm,
    handleSearchOnChange,
    handleChangeHumanAgent,
  } = useQAResumeListViewModel();

  const {
    getValues,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = formInfo;
  const data = result?.data || [];
  console.log("Data:", data);
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  return (
    <div className="qa_resume_list_page">
      <LayoutComponent title={i18n(KeyWordLocalization.QAResumeListTitle)}>
        <div className="container py-5">
          <form onSubmit={handleSubmit(_handleSearch)}>
            <div className="row">
              <div className="col-md-12 my-2">
                <AutoCompleteWithCheckComponent
                  multiple={false}
                  keyName={KeyWordLocalization.QAResumeListPageHumanAgent}
                  label={i18n(KeyWordLocalization.QAResumeListPageHumanAgent)}
                  formInfo={formInfo}
                  onSearch={_onSearchHuman}
                  onChange={handleChangeHumanAgent}
                />
              </div>

              <div className="col-md-12 col-lg-4 my-2">
                <AutoCompleteWithCheckComponent
                  multiple={false}
                  keyName={KeyWordLocalization.QAResumeEntityJobStatus}
                  label={i18n("Call Status")}
                  formInfo={formInfo}
                  onSearch={_onSearchCallState}
                  onChange={() => handleSearchOnChange()}
                />
              </div>

              <div className="col-md-12 col-lg-4 my-2">
                <AutoCompleteWithCheckComponent
                  multiple={false}
                  keyName={KeyWordLocalization.QAResumeEntityCustomerSentiment}
                  label={i18n("Sentiment")}
                  formInfo={formInfo}
                  onSearch={_onSearchSentiment}
                  onChange={() => handleSearchOnChange()}
                />
              </div>


              <div className="col-md-12 col-lg-4 my-2">
                <label>{i18n("Audio Name")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={i18n("Search by audio name")}
                  {...register("audio_name")}
                  onChange={(e) => {
                    formInfo.setValue("audio_name", e.target.value);
                    handleSearchOnChange();
                  }}
                />
              </div>


              <div className="col-12 col-md-4 my-2">
                <label>{i18n(KeyWordLocalization.QAResumeListPageFrom)}</label>
                <input
                  type="date"
                  min={new Date("2021-01-01").toISOString().split("T")[0]}
                  className="form-control"
                  {...register(
                    KeyWordLocalization.QAResumeListPageFrom,
                    Validators({
                      lessThan: new Date(),
                      onChange: handleOnChangeForm,
                    })
                  )}
                />
                <ErrorMessage
                  as={"aside"}
                  errors={errors}
                  name={KeyWordLocalization.QAResumeListPageFrom}
                />
              </div>

              <div className="col-12 col-md-4 my-2">
                <label>{i18n(KeyWordLocalization.QAResumeListPageTo)}</label>
                <input
                  min={
                    watch(KeyWordLocalization.QAResumeListPageFrom) ||
                    new Date("2021-01-01")
                  }
                  max={new Date().toISOString().split("T")[0]}
                  type="date"
                  className="form-control"
                  {...register(
                    KeyWordLocalization.QAResumeListPageTo,
                    Validators({
                      lessThan: new Date(),
                      onChange: handleOnChangeForm,
                      greaterOrEqualThan: new Date(
                        watch(KeyWordLocalization.QAResumeListPageFrom) ||
                        "2021-01-01"
                      ),
                    })
                  )}
                />
                <ErrorMessage
                  as={"aside"}
                  errors={errors}
                  name={KeyWordLocalization.QAResumeListPageTo}
                />
              </div>

              <div className="col-12 col-md-4 my-2">
                <div className="d-flex gap-2 align-items-start mt-4">
                  <ButtonComponent
                    type="submit"
                    icon={<span className="material-symbols-outlined">search</span>}
                  />
                  <ButtonComponent
                    design="primaryOutline"
                    isLoading={loadingDownload}
                    onClick={handleDownloadData}
                    icon={<span className="material-symbols-outlined">download</span>}
                  />

                  <ButtonComponent
                    design="dangerOutline"
                    onClick={() => {
                      reset({
                        [KeyWordLocalization.QAResumeListPageHumanAgent]: [],
                        [KeyWordLocalization.QAResumeEntityJobStatus]: [],
                        [KeyWordLocalization.QAResumeEntityCustomerSentiment]: [],
                        audio_name: "",
                        [KeyWordLocalization.QAResumeListPageFrom]: "",
                        [KeyWordLocalization.QAResumeListPageTo]: "",
                      });
                      setCurrentPage(1);
                      handleSubmit(_handleSearch)();
                    }}
                    icon={<span className="material-symbols-outlined">refresh</span>}
                  />

                  <div className="dropdown w-100">
                    <button
                      className="btn btn-outline-secondary dropdown-toggle w-100"
                      type="button"
                      onClick={() => setShowDropdown(!showDropdown)}
                    >
                      Columns
                    </button>

                    {showDropdown && (
                      <div
                        ref={dropdownRef}
                        className="dropdown-menu show p-3"
                        style={{
                          maxHeight: "970px",
                          overflowY: "auto",
                          position: "absolute",
                          zIndex: 999,
                        }}
                      >
                        {allColumns.map((col) => (
                          <div key={col.key} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`col-${col.key}`}
                              checked={visibleColumns.includes(col.key)}
                              onChange={() => toggleColumn(col.key)}
                            />
                            <label className="form-check-label" htmlFor={`col-${col.key}`}>
                              {col.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>

          <div className="list mt-4" style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
            {result == undefined && (
              <div className="mt-5">
                <LoadingComponent />
              </div>
            )}
            {result && result.data.length == 0 && <NotResultsComponent />}
            {result && result.data.length > 0 && (
              <div className="w-100">
                <div
                  className="overflow-x-auto"
                  style={{ maxHeight: "600px", overflowX: "auto", overflowY: "auto" }}
                >
                  <table
                    className="table my-3"
                    style={{ minWidth: "800px", tableLayout: "auto" }}
                  >
                    <thead>
                      <tr>
                        {allColumns.map(
                          (col) =>
                            visibleColumns.includes(col.key) && (
                              <th key={col.key}>
                                {i18n(
                                  KeyWordLocalization[
                                  `QAResumeEntity${col.label.replace(/\s/g, "")}` as keyof typeof KeyWordLocalization
                                  ] || col.label
                                )}
                              </th>
                            )
                        )}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.data.slice(startIndex, endIndex).map((resume, index) => (
                        <tr key={index} className="hover" onClick={() => handleShowModal(resume)}>
                          {allColumns.map(
                            (col) =>
                              visibleColumns.includes(col.key) && (
                                <td key={col.key}>
                                  {(() => {
                                    switch (col.key) {
                                      case "created_at":
                                        return new Date(resume.created_at).toLocaleString();
                                      case "agent_score":
                                      case "customer_score":
                                      case "average_score":
                                        return resume[col.key].toFixed(1) + "%";
                                      case "target":
                                      case "achieved":
                                        return resume[col.key].toFixed(0);
                                      default:
                                        return resume[col.key];
                                    }
                                  })()}
                                </td>
                              )
                          )}
                          <td>
                            <ButtonComponent
                              design="primaryOutline"
                              onClick={(e) => {
                                e.stopPropagation();
                                _handleOpenVisibility(resume.job_id);
                              }}
                              icon={<span className="material-symbols-outlined">visibility</span>}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Selector de elementos por página */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-3">
                  <div className="d-flex align-items-center">
                    <label className="me-2 mb-0">{i18n("Items per page:")}</label>
                    <select
                      className="form-select"
                      style={{ width: "100px" }}
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </select>
                  </div>

                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-outline-primary me-2"
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage <= 1}
                    >
                      {i18n("Previous")}
                    </button>
                    <span>
                      {i18n("Page")} {currentPage} {i18n("of")} {totalPages}
                    </span>
                    <button
                      className="btn btn-outline-primary ms-2"
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage >= totalPages}
                    >
                      {i18n("Next")}
                    </button>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      </LayoutComponent>
    </div>

  );
};

export default QAResumeListPage;
